import { RadioGroup as ARadioGroup } from '@ark-ui/solid/radio-group';
import { Show, splitProps } from 'solid-js';
import { cn } from 'tailwind-variants';
import { Field } from './field.tsx';
import type { ComponentProps, JSXElement } from 'solid-js';

export function Radio(props: ComponentProps<typeof ARadioGroup.Item>) {
	return (
		<ARadioGroup.Item {...props} class="group/radio relative flex items-center-safe gap-x-2.5">
			<ARadioGroup.ItemHiddenInput tabindex={props.tabindex ?? 0} />
			<ARadioGroup.ItemControl class="relative mt-1 size-6 shrink-0 appearance-none self-baseline rounded-full border border-zinc-300 bg-white transition-all duration-200 outline-none group-hover/radio:bg-yellow-500/50 group-has-focus-visible/radio:ring-4 group-has-focus-visible/radio:ring-yellow-500/50 group-has-focus-visible/radio:ring-offset-2 group-has-focus-visible/radio:ring-offset-zinc-50 after:absolute after:inset-0 after:m-auto after:size-2 after:rounded-full after:bg-white after:transition-all after:duration-200 after:ring-inset focus-visible:ring-1 focus-visible:ring-yellow-500/50 active:bg-yellow-500 active:after:scale-150 active:after:bg-yellow-100 data-[state=checked]:border-yellow-500 data-[state=checked]:bg-white data-[state=checked]:after:scale-200 data-[state=checked]:after:bg-yellow-500 motion-safe:data-disabled:group-has-active/radio:animate-shake dark:border-zinc-700 dark:bg-black dark:group-has-focus-visible/radio:ring-offset-zinc-950 dark:after:bg-black dark:group-hover/radio:after:bg-zinc-950/50 dark:data-[state=checked]:bg-black dark:data-[state=checked]:after:bg-yellow-500" />
			<div class="flex shrink flex-col gap-0.5">{props.children}</div>
		</ARadioGroup.Item>
	);
}

Radio.Label = (props: ComponentProps<typeof ARadioGroup.ItemText>) => (
	<ARadioGroup.ItemText {...props} class="text-lg font-medium" />
);

Radio.Group = (props: ComponentProps<typeof ARadioGroup.Root> & { name: string }) => {
	return (
		<Field name={props.name} class={props.class}>
			<ARadioGroup.Root {...props} class="flex flex-col gap-y-3">
				{props.children}
			</ARadioGroup.Root>
		</Field>
	);
};

Radio.GroupLabel = (props: ComponentProps<typeof ARadioGroup.Label>) => (
	<ARadioGroup.Label {...props} class={cn('text-lg font-medium', props.class)}>
		{props.children}
		<Field.Required />
	</ARadioGroup.Label>
);

export function RadioField(props: ComponentProps<typeof Radio> & { label: JSXElement; description?: JSXElement }) {
	const [other, rest] = splitProps(props, ['label', 'description']);
	return (
		<Radio {...rest}>
			<Radio.Label>{other.label}</Radio.Label>
			<Show when={other.description}>{(desc) => <Field.Description>{desc()}</Field.Description>}</Show>
		</Radio>
	);
}

export function RadioGroupField(
	props: ComponentProps<typeof Radio.Group> & { name: string; label: JSXElement; description?: JSXElement },
) {
	const [other, rest] = splitProps(props, ['label', 'description']);
	return (
		<Radio.Group {...rest}>
			<Radio.GroupLabel>{other.label}</Radio.GroupLabel>
			{props.children}
			<Field.Errors />
			<Show when={other.description}>{(desc) => <Field.Description>{desc()}</Field.Description>}</Show>
		</Radio.Group>
	);
}
